import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EconomicsGroupNode } from '../../types/economicsTypes';
import { EconomicsCheckbox } from './EconomicsCheckbox';
import { ReimbursementOverrideModal } from './ReimbursementOverrideModal';
import { formatDkk } from '../../utils/currencyUtils';
import { formatDate } from '../../utils/dateUtils';
import EditIcon from '../../assets/icons/edit.svg?react';
import styles from './Economics.module.css';

interface EconomicsGroupRowProps {
  node: EconomicsGroupNode;
  canWrite: boolean;
  onToggle: (key: string, completed: boolean) => void;
  onOverrideChange: (key: string, value: number | null, note: string | null) => void;
}

export const EconomicsGroupRow = ({
  node,
  canWrite,
  onToggle,
  onOverrideChange
}: EconomicsGroupRowProps) => {
  const { t, i18n } = useTranslation();
  const [isEditingAmount, setIsEditingAmount] = useState(false);

  let dateLabel = '—';
  if (node.completed) {
    dateLabel = node.completedAt
      ? formatDate(node.completedAt, i18n.language)
      : t('economics-unsaved');
  }

  const handleConfirm = (value: number | null, note: string): void => {
    onOverrideChange(node.key, value, note);
    setIsEditingAmount(false);
  };

  return (
    <div className={`${styles.row} ${styles.rowGroup}`}>
      <span className={styles.spacer} />
      <EconomicsCheckbox
        state={node.completed ? 'all' : 'none'}
        onChange={(next) => onToggle(node.key, next)}
        disabled={!canWrite}
        ariaLabel={t('economics-complete-group', { group: node.groupName })}
      />
      <div className={styles.label}>
        <span>{node.groupName}</span>
        <span className={styles.week}>{t('economics-week-label', { week: node.weekNumber })}</span>
      </div>
      <div className={styles.spread}>
        <span className={styles.date}>{dateLabel}</span>
        <div className={styles.amountCell}>
          <span className={`${styles.amount} ${node.completed ? '' : styles.amountMuted}`}>
            {formatDkk(node.effectiveAmount, i18n.language)}
          </span>
          {node.isOverridden && (
            <span className={styles.overrideTag} title={node.note ?? undefined}>
              {t('economics-overridden')}
            </span>
          )}
          {node.completed && canWrite && (
            <button
              type="button"
              className={styles.editAmountButton}
              onClick={() => setIsEditingAmount(true)}
              aria-label={t('economics-edit-amount', { group: node.groupName })}
            >
              <EditIcon className={styles.editIcon} />
            </button>
          )}
        </div>
      </div>

      {isEditingAmount && (
        <ReimbursementOverrideModal
          node={node}
          onClose={() => setIsEditingAmount(false)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
};
